// These helpers are calling FTW's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import { types as sdkTypes, transit } from './sdkLoader';
import config from '../config';
import Decimal from 'decimal.js';

export const apiBaseUrl = () => {
    const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
    const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

    // In development, the dev API server is running in a different port
    if (useDevApiServer) {
        return `http://localhost:${port}`;
    }

    // Otherwise, use the same domain and port as the frontend
    return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
    // Use Decimal type instead of SDK's BigDecimal.
    {
        type: sdkTypes.BigDecimal,
        customType: Decimal,
        writer: (v) => new sdkTypes.BigDecimal(v.toString()),
        reader: (v) => new Decimal(v.value),
    },
];

const serialize = (data) => {
    return transit.write(data, {
        typeHandlers,
        verbose: config.sdk.transitVerbose,
    });
};

const deserialize = (str) => {
    return transit.read(str, { typeHandlers });
};

const post = (path, body, requestOptions) => {
    const url = `${apiBaseUrl()}${path}`;
    const { skipHeaders } = requestOptions || {};
    const headersMaybe = skipHeaders ? {} : {
        'Content-Type': 'application/transit+json',
    };
    const bodyMaybe = skipHeaders ? body : serialize(body);

    const options = {
        method: 'POST',
        credentials: 'include',
        headers: headersMaybe,
        body: bodyMaybe,
    };

    let globalRes = null;
    return window.fetch(url, options).then((res) => {
        globalRes = res;
        const contentTypeHeader = res.headers.get('Content-Type');
        const contentType = contentTypeHeader
            ? contentTypeHeader.split(';')[0]
            : null;

        if (res.status >= 400) {
            return res.json().then((data) => {
                let e = new Error();
                e = Object.assign(e, data);

                throw e;
            });
        }
        if (contentType === 'application/transit+json') {
            return res.text().then(deserialize).catch(() => {});
        } else if (contentType === 'application/json') {
            return res.json();
        }
        return res.text();
    }).catch((error) => {
        let e = new Error();
        e = Object.assign(e, globalRes);

        throw e;
    });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = (body) => {
    return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the booking data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = (body) => {
    return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = (body) => {
    return post('/api/transition-privileged', body);
};

export const updateMetadata = body => {
    return post('/api/update-metadata', body);
};

export const sendSGMail = body => {
    return post('/api/send-mail', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Flex with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = (body) => {
    return post('/api/auth/create-user-with-idp', body);
};

export const fetchFiles = body => {
    return post('/api/files/get', body);
};

export const uploadFile = body => {
    return post('/api/files/upload', body, {
        skipHeaders: true,
    });
};

export const fetchSysMsgs = body => {
    return post('/api/tx-sys-msg/get', body);
};

export const createSysMsg = body => {
    return post('/api/tx-sys-msg/create', body);
};

export const fetchOffers = body => {
    return post('/api/offers/get', body);
};

export const createOffer = body => {
    return post('/api/offers/create', body);
};

export const updateOffer = body => {
    return post('/api/offers/update', body);
};

export const createEmailReminder = body => {
    return post('/api/reminder-emails/create', body);
};
